import React, {
  FunctionComponent,
  useCallback,
  useState,
  ChangeEvent,
} from 'react';
import styled from 'styled-components';

import { LoaderModal, Modal } from 'components';
import { Input, Button, FormGroup } from 'components/chroma';
import {
  APOST_EMAIL_MSG,
  checkEmailForSingleQuote,
} from 'scenes/common/helpers/validation';

const SAVE_QUOTE_EMAIL_INPUT = 'saveQuoteEmailInput';
const SAVE_QUOTE_BUTTON = 'saveQuoteButton';
const HELP_TEXT_60_DAYS =
  'Save your quote for 60 days and access it at anytime. We will only ever use this to contact you regarding this particular quote.';
const HELP_TEXT_FIXED_DATE =
  'Retrieve your quote up until the 31st August 2024. We will only ever use this to contact you regarding this particular quote.';

const VALID_EMAIL_PATTERN = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;

const getHelpText = (isBeforeEffectiveEndDate: Boolean) =>
  isBeforeEffectiveEndDate ? HELP_TEXT_60_DAYS : HELP_TEXT_FIXED_DATE;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveQuote: (emailAddress: string) => void;
  contactEmailAddress: string;
  renderSaveQuoteComplete: () => JSX.Element;
  isQuoteSaved: boolean;
  isQuoteSaveInProgress: boolean;
  isBeforeEffectiveEndDate: boolean;
}

const ModalInputContainer = styled.div`
  margin: 0 0 1.5rem;
`;

const SaveQuoteModal: FunctionComponent<ModalProps> = ({
  isOpen,
  onClose,
  onSaveQuote,
  isQuoteSaved,
  contactEmailAddress,
  isQuoteSaveInProgress,
  renderSaveQuoteComplete,
  isBeforeEffectiveEndDate,
}) => {
  const [emailAddress, setEmailAddress] = useState(contactEmailAddress);
  const [inputError, setInputError] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value?.trim();
    setEmailAddress(value);
    setInputError(
      checkEmailForSingleQuote(value) || !value.match(VALID_EMAIL_PATTERN)
    );
  };

  const onSaveQuoteSubmit = useCallback(() => {
    onSaveQuote(emailAddress);
  }, [emailAddress, onSaveQuote]);

  return (
    <Modal header="" isOpen={isOpen} onClose={onClose}>
      <LoaderModal isOpen={isQuoteSaveInProgress} />
      {!isQuoteSaved && (
        <>
          <ModalInputContainer>
            <FormGroup
              label="Email address"
              helpText={getHelpText(isBeforeEffectiveEndDate)}
              error={
                checkEmailForSingleQuote(emailAddress) ? APOST_EMAIL_MSG : ''
              }
            >
              <Input
                id={SAVE_QUOTE_EMAIL_INPUT}
                name={SAVE_QUOTE_EMAIL_INPUT}
                autoComplete="email"
                value={emailAddress}
                onChange={handleChange}
                placeholder="Enter email address"
                valid={Boolean(emailAddress && !inputError)}
                error={inputError}
                aria-required
              />
            </FormGroup>
          </ModalInputContainer>
          <Button
            id={SAVE_QUOTE_BUTTON}
            disabled={!emailAddress || inputError}
            onClick={onSaveQuoteSubmit}
            type="submit"
            primary
            size="lg"
            maxWidth
          >
            Save your quote
          </Button>
        </>
      )}
      {isQuoteSaved && renderSaveQuoteComplete && renderSaveQuoteComplete()}
    </Modal>
  );
};

export default SaveQuoteModal;
